import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import { environment } from "../../../environments/environment";
import { Observable } from "rxjs/internal/Observable";
import { Subject } from "rxjs/internal/Subject";
import { shareReplay, tap } from 'rxjs/operators';
import { PeriodData } from "../../components/dashboards/period-filter/period-filter";
import { groupBy } from "../../constants/globalConstants";
import { ToastrService } from "ngx-toastr";
import { EngagementDetails } from "../../components/dashboards/qrs-survey-form/qrs-survey-form";
export enum DashboardTypes {
  LER = 'LER',
  QChamp = 'QChamp',
  ProgramAdmin = 'PA',
  QRM = 'QRM'
}

export interface LerCheckIn {
  Id: number;
  CheckInDate: string;
  LERSummary: string;
  CreatedDate: string;
  ModifiedDate: string | null;
  ForRedReview: boolean;
}

export interface LerCheckInSaveRequest {
  Id?: number | null;
  EngagementId: number;
  CheckInDate: string;
  LERSummary: string;
  ForRedReview?: boolean;
}

export interface LerCheckInFrequency {
  EngagementId: number;
  Frequency: string;
  UpdatedBy?: string | null;
  UpdatedDate?: string | null;
}
export interface DelegateUser {
  IsDeleted: boolean;
  DU_ID: string | null;
  DU_NAME: string;
  DU_EMAIL_ID: string;
  DU_TITLE?: string;
  IsSelected: boolean;
  newAddedValue: boolean;
  MasterKeyId: string;
  ProgramId: number;
}

export interface QRSSendSurveyRequest {
  isDoNotSend: boolean;
  Reason: string;
  LerEmail: string;
  LepEmail: string;
  isSendMail: boolean;
  QrsIds?: number[];
  PeriodId?: string;
  QmtId?: string;
  EngagementId?: string;
  EngagementIds?: number[];
}

export interface QRSSaveLerChangeRequest {
  QRMId: string;
  LerEmail: string;
  LerName: string;
}

export interface QRSSaveQChampChangeRequest {
  QRMId: string;
  QChampEmail: string;
  QChampName: string;
}

export interface QRSSaveQRMRoleChangeRequest {
  QRMId: string;
  QRMRoleEmail: string;
  QRMRoleName: string;
}

export interface EngagementData {
  MasterKeyId: string;
  ProgramId: number;
  IsMsnpMND?: string;
}

export interface DelegateMailRequest {
  DELEGATE_USER_LIST: Array<{
    QU_ID: string | null;
    QU_NAME: string;
    QU_EMAIL: string;
  }>;
  LOGGED_IN_USER: string;
  PROGRAM_ID: number;
  ENTITY_ID: string;
  IS_DELEGATE: boolean;
}

export interface LerOption {
  Email: string;
  Name: string;
  MofCodes: string[];
}

export interface QChampOption {
  Email: string;
  Name: string;
  MofCodes: string[];
}

export interface QRMRoleOption {
  Email: string;
  Name: string;
  MofCodes: string[];
}

export interface PersonSearchResult {
  givenName: string;
  surname: string;
  jobTitle: string;
  mail: string;
}
export interface ElDisplayDetail {
  EngagementLeader?: string;
  ClientName?: string;
  SolutionName?: string;
  DapId?: string;
  OpportunityId?: string;
  EngagementName?: string;
  ProgramName?: string;
  MercuryId?: string;
}

export interface ElMasterData {
  Offeringsportfolio: Array<any>;
  Offerings: Array<any>;
  ELDashboardStaticData: Array<any>;
  SolutionMasterList: Array<any>;
  ELDashboardMasterData: Array<any>;
  ELDashboardRolling13Periods: Array<any>;
  ELDashboardPeriodsWithDates: Array<any>;
  delegateUserList: Array<any>;
  ELDashboardTextLabelChanges: Array<any>;
}
export interface LEPMasterData {
  Offeringsportfolio: Array<any>;
  Offerings: Array<any>;
  ELDashboardStaticData: Array<any>;
  SolutionMasterList: Array<any>;
  ELDashboardMasterData: Array<any>;
  ELDashboardRolling13Periods: Array<any>;
  ELDashboardPeriodsWithDates: Array<any>;
  delegateUserList: Array<any>;
  ELDashboardTextLabelChanges: Array<any>;
  LEPDashboardData?: Array<any>;
}
@Injectable({
  providedIn: 'root',
})
export class DashboardService {

  constructor(private http: HttpClient, private toasterService: ToastrService) { }

  selectedEngagement = signal<EngagementDetails | null>(null);

  // Replacements for $scope.$on channels
  private oplStaticDataSubject = new Subject<any[]>();
  oplStaticData$ = this.oplStaticDataSubject.asObservable();

  private elStaticDataSubject = new Subject<any[]>();
  elStaticData$ = this.elStaticDataSubject.asObservable();

  private elDataSubject = new Subject<any[]>();
  elData$ = this.elDataSubject.asObservable();

  private elMasterDataSubject = new Subject<any[]>();
  elMasterData$ = this.elMasterDataSubject.asObservable();

  private periodFilterApplySubject = new Subject<string>();
  periodFilterApply$ = this.periodFilterApplySubject.asObservable();

  private resetSearchSubject = new Subject<void>();
  resetSearch$ = this.resetSearchSubject.asObservable();

  // Replacement for $rootScope.$broadcast('elseachname', ...)
  private searchEmitSubject = new Subject<{ term: string; fromFillText?: boolean }>();
  search$ = this.searchEmitSubject.asObservable();

  private qrsSearchDataSubject = new Subject<any[]>();
  qrsSearchData$ = this.qrsSearchDataSubject.asObservable();

  emitOplStatic(data: any[]) { this.oplStaticDataSubject.next(data); }
  emitElStatic(data: any[]) { this.elStaticDataSubject.next(data); }
  emitElData(data: any[]) { this.elDataSubject.next(data); }
  emitElMasterData(data: any[]) { this.elMasterDataSubject.next(data); }
  emitQrsSearchData(data: any[]) { this.qrsSearchDataSubject.next(data); }
  emitPeriodFilterApply(message: string) { this.periodFilterApplySubject.next(message); }
  emitResetSearch() { this.resetSearchSubject.next(); }
  emitSearch(term: string, fromFillText?: boolean) { this.searchEmitSubject.next({ term, fromFillText }); }

  // Utility to remove duplicates analogous to original JSON stringify group
  uniqueByValue<T>(arr: T[]): T[] {
    const map = new Map<string, T>();
    for (const item of arr) {
      const key = JSON.stringify(item);
      if (!map.has(key)) map.set(key, item);
    }
    return Array.from(map.values());
  }

  filterMasterDataByPeriodType(periodData: PeriodData, currentFiscalYear: string, MasterData: any[], Rolling13: any[], PeriodsWithDates: any[] = []): any[] {
    const periodType = periodData.periodType;
    const period = periodData.period;
    const fromDate = periodData.fromDate;
    const toDate = periodData.toDate;

    // Group master data by ProgramId
    const grpMasterDataByProgram: { [key: string]: any[] } = groupBy(MasterData, 'ProgramId');
    // Group periods by ProgramId
    const grpPeriodsByProgram: { [key: string]: any[] } = groupBy(Rolling13, 'ProgramId');

    let filteredData: any[] = [];

    if (periodType === 'rolling13') {
      for (const programId in grpPeriodsByProgram) {
        const r13Periods = grpPeriodsByProgram[programId].map(row => row['Period']);
        for (const row of grpMasterDataByProgram[programId] || []) {
          if (r13Periods.includes(row['Period'])) {
            filteredData.push(row);
          }
        }
      }
    } else if (periodType === 'lTillDate') {
      filteredData = [...MasterData];
    } else if (periodType === 'yearPeriod') {
      const yrPeriods = (typeof period === 'string' ? period.split('|') : period).map((item: string) => item.trim());
      for (const programId in grpMasterDataByProgram) {
        for (const row of grpMasterDataByProgram[programId]) {
          if (yrPeriods.includes(row['Period'])) {
            filteredData.push(row);
          }
        }
      }
    } else if (periodType === 'yTillDate') {
      const ytdPeriods = MasterData
        .filter(row => row['Period'].trim().toUpperCase().includes(currentFiscalYear.trim().toUpperCase()))
        .map(row => row['Period']);
      for (const programId in grpMasterDataByProgram) {
        for (const row of grpMasterDataByProgram[programId]) {
          if (ytdPeriods.includes(row['Period'])) {
            filteredData.push(row);
          }
        }
      }
    } else if (periodType === 'custom') {
      const dateSource = PeriodsWithDates.length > 0 ? PeriodsWithDates : MasterData;
      const customPeriods = dateSource
        .filter(row =>
          new Date(row['FromDate']) >= new Date(fromDate) &&
          new Date(row['ToDate']) <= new Date(toDate)
        )
        .map(row => row['Period']);
      for (const programId in grpMasterDataByProgram) {
        for (const row of grpMasterDataByProgram[programId]) {
          if (customPeriods.includes(row['Period'])) {
            filteredData.push(row);
          }
        }
      }
    }

    return filteredData;
  }

  getDataForOfferingPortfolioDropdown = function (Offerings: any[], Offeringsportfolio: any[], SolutionMasterList: any[]): any[] {

    const opLeaderDropDownOptions: any[] = [];
    const opLeaderMoSolution: any[] = [];

    // Build opLeaderMoSolution array
    for (const item of Offeringsportfolio) {
      if (item.OfferingId != null && item.OfferingId != undefined) {
        opLeaderMoSolution.push({
          OfferingCode: item.OfferingId,
          Offering: item.OfferingValue,
          offeringPortfolioId: item.PAR_CODE,
          solutionData: [],
          isCheckedbkp: false
        });
      }
    }

    // Build opLeaderDropDownOptions array
    for (const item of Offerings) {
      if (item.Id != null && item.Id != undefined) {
        opLeaderDropDownOptions.push({
          offeringPortfolioCode: item.Id,
          offeringPortfolio: item.Value,
          offeringData: [],
          isCheckedbkp: false
        });
      }
    }

    // Populate solutionData for each offering in opLeaderMoSolution
    SolutionMasterList.forEach((solutionData: any) => {
      opLeaderMoSolution.forEach((offeringData: any) => {
        if (solutionData.OfferingCode === offeringData.OfferingCode) {
          offeringData.solutionData.push(solutionData);
        }
      });
    });

    // Populate offeringData for each portfolio in opLeaderDropDownOptions
    opLeaderDropDownOptions.forEach((portfolio: any) => {
      opLeaderMoSolution.forEach((offering: any) => {
        if (portfolio.offeringPortfolioCode === offering.offeringPortfolioId) {
          portfolio.offeringData.push(offering);
        }
      });
    });

    return opLeaderDropDownOptions;

  }
  getPrograms(): Observable<any> {
    const now = Date.now();
    
    // Return cached value if within TTL
    if (this.programInfo$ && 
        (now - this.programInfoCacheTime) < this.PROGRAM_INFO_CACHE_TTL) {
      return this.programInfo$;
    }
    
    // Fetch fresh data and cache it
    this.programInfo$ = this.http.get(`${environment.apiBaseUrl}/dashboard/GetProgramInfo`).pipe(
      shareReplay(1)
    );
    this.programInfoCacheTime = now;
    
    return this.programInfo$;
  }
  
  // Method to invalidate program info cache (call after updates)
  invalidateProgramInfoCache(): void {
    this.programInfoCacheTime = 0;
    this.programInfo$ = null;
  }

  getQRSTriageData(dashboard:string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/dashboard/getQRSTriageData`,{
      params: {
        dashboardName: dashboard
      }
    });
  }

  getQRSUserByOffering(roleName: string): Observable<LerOption[]> {
    return this.http.get<LerOption[]>(`${environment.apiBaseUrl}/dashboard/GetQRSUserByOffering`, {
      params: { roleName }
    });
  }

  qrsSaveLerChange(payload: QRSSaveLerChangeRequest): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/QRSChangeLerAssignment`, payload);
  }
  getQRSUserBySolutionOffering(roleName: string): Observable<LerOption[]> {
    return this.http.get<LerOption[]>(`${environment.apiBaseUrl}/dashboard/GetQRSUserBySolutionOffering`, {
      params: { roleName }
    });
  }
  qrsSaveQChampChange(payload: QRSSaveQChampChangeRequest): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/QRSChangeQChampAssignment`, payload);
  }

  qrsSaveQRMRoleChange(payload: QRSSaveQRMRoleChangeRequest): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/QRSChangeQRMRoleAssignment`, payload);
  }

  searchEngagements(term: string, subCategoryId?: string | null): Observable<any[]> {
    const params: any = { query: term };
    if (subCategoryId) {
      params.subCategoryId = subCategoryId;
    }
    return this.http.get<any[]>(`${environment.apiBaseUrl}/dashboard/FindQRSEngagement`, {
      params
    });
  }

  qrsSendSurvey(payload: QRSSendSurveyRequest): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/QRSSendSurvey`, payload);
  }

  qrsSendMultipleSurvey(payloads: QRSSendSurveyRequest[]): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/QRSSendMultipleSurvey`, payloads);
  }

  qrsSaveCheckInActionItem(payload: { ID?: number | null; EngagementId: number; Description: string; Status: boolean; OutcomeOfAction?: string; ForRedReview: boolean }): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/QRSSaveCheckInActionItem`, payload);
  }

  getQRSCheckInActionItem(engagementId: number): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/dashboard/GetQRSCheckInActionItem`, {
      params: { EngagementId: engagementId.toString() }
    });
  }

  qrsDeleteCheckInActionItem(actionItemId: number): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/QRSDeleteCheckInActionItem`, actionItemId);
  }

  getLerCheckIns(engagementId: number): Observable<LerCheckIn[]> {
    return this.http.get<LerCheckIn[]>(`${environment.apiBaseUrl}/dashboard/GetQRSLerCheckIn`, {
      params: { engagementId: engagementId.toString() }
    });
  }

  saveLerCheckIn(request: LerCheckInSaveRequest): Observable<boolean> {
    return this.http.post<boolean>(`${environment.apiBaseUrl}/dashboard/QRSSaveLerCheckIn`, request);
  }

  deleteLerCheckIn(id: number): Observable<boolean> {
    return this.http.post<boolean>(`${environment.apiBaseUrl}/dashboard/QRSDeleteLerCheckIn`, id);
  }

  getLerCheckInFrequency(engagementId: number): Observable<LerCheckInFrequency | null> {
    return this.http.get<LerCheckInFrequency | null>(`${environment.apiBaseUrl}/dashboard/GetQRSLerCheckInFrequency`, {
      params: { engagementId: engagementId.toString() }
    });
  }

  saveLerCheckInFrequency(payload: { EngagementId: number; Frequency: string }): Observable<boolean> {
    return this.http.post<boolean>(`${environment.apiBaseUrl}/dashboard/QRSSaveLerCheckInFrequency`, payload);
  }

  qrsRescheduleOrCompleteCheckIn(payload: { EngagementId: number; NextReviewDate: string; Notes: string; IsCheckIn: boolean; IsComplete: boolean; QmtIds?: number[]; }): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/QRSResheduleOrCompleteCheckIn`, payload);
  }

  //Engagement Leader Dashboard
  getLepMasterData(email: string, isRolling13: boolean) {
    return this.http.post<LEPMasterData>(`${environment.apiBaseUrl}/dashboard/GetLEPDashboardMasterData`, { UserId: email, isRolling13: false, isRiskCategoryDashboard: false });
  }

  qrsSaveRedReviewSummary(payload: { EngagementId: number; Summary: string; IsDeescalate: boolean; QmtIds?: number[] }): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/SaveRedReviewHistory`, payload);
  }

  //El Dashboard
  getElMasterData(email: string, isRolling13: boolean) {
    return this.http.post<ElMasterData>(`${environment.apiBaseUrl}/dashboard/GetELDashboardMasterData`, { UserId: email, isRolling13: isRolling13, isRiskCategoryDashboard: false });
  }

  saveDelegateUserDetails(data: DelegateMailRequest): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/Mail/SaveDelegateUserDetails`, data);
  }

  getMailDetails(data: DelegateMailRequest): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/Mail/GetMailDetails`, data);
  }

  sendMailDelegateUser(data: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/Mail/SendMailDelegateUser`, data);
  }
  //LERDashboard
  getLERDashboardMasterData(solutionCodes: string | null, isRolling13: boolean): Observable<OpLeaderMasterData> {
    return this.http.post<OpLeaderMasterData>(`${environment.apiBaseUrl}/dashboard/GetLERDashboardMasterData`, {
      'SubCategoryId': solutionCodes,
      'isRolling13': isRolling13
    });
  }
  //SOLDashboard
  getOpLeaderMasterData(solutionCodes: string | null, isRolling13: boolean, isHybrid: boolean): Observable<OpLeaderMasterData> {
    return this.http.post<OpLeaderMasterData>(`${environment.apiBaseUrl}/dashboard/GetOPLeaderMasterData`, {
      'SubCategoryId': solutionCodes,
      'isRolling13': isRolling13,
      'isHybrid': isHybrid
    });
  }
  getRiskLeaderMasterData(programList: string | null, solutionCodes: string | null, isRolling13: boolean, isHybrid: boolean): Observable<OpLeaderMasterData> {
    return this.http.post<OpLeaderMasterData>(`${environment.apiBaseUrl}/dashboard/GetRiskCategoryData`, {
      'SubCategoryId': solutionCodes,
      'programList': programList,
      'isRiskCategoryDashboard': true,
      'isRolling13': isRolling13,
      'isHybrid': isHybrid
    });
  }

  getEmployeeImages(subCategoryId: string | null, isRolling13: boolean): Observable<Array<EmployeeImageData>> {
    return this.http.post<Array<EmployeeImageData>>(`${environment.apiBaseUrl}/dashboard/GetOPLeaderMasterImageData`, {
      'SubCategoryId': subCategoryId,
      isRolling13
    });
  }

  async downloadExcelFile(exportPayload: any): Promise<void> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    try {
      const response = await this.http.post<{ ByteArray: string, ContentType: string, FileName: string }>(`${environment.apiBaseUrl}/dashboard/GetExcelDownload`, exportPayload, {
        headers
      }).toPromise();

      if (response && response.ByteArray) {
        // Convert base64 ByteArray to Uint8Array
        const byteCharacters = atob(response.ByteArray);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        // Use the provided filename or fallback
        let fileName = response.FileName || 'dashboard_export.xlsx';

        // Ensure .xlsx extension if not present
        if (!fileName.toLowerCase().endsWith('.xlsx')) {
          fileName += '.xlsx';
        }

        // Create blob with the provided content type or default
        const contentType = response.ContentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const blob = new Blob([byteArray], { type: contentType });

        this.downloadBlob(blob, fileName);
        this.toasterService.success('Report downloaded successfully.');
      }
    } catch (error) {
      console.error('Download error:', error);
      this.toasterService.error('Failed to download report.');
      throw error;
    }
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    // Sanitize filename before assigning to DOM element to prevent DOMXSS
    const safeFileName = String(fileName || 'dashboard_export.xlsx')
      .replace(/[<>"'`]/g, '')
      .replace(/[^\w\s.\-_()[\]]/g, '')
      .trim() || 'dashboard_export.xlsx';
    // Check for IE/Edge browser
    if ((window.navigator as any).msSaveBlob) {
      (window.navigator as any).msSaveBlob(blob, safeFileName);
    } else {
      // Modern browsers — click detached anchor (no appendChild needed)
      const objectUrl = (window.URL || (window as any).webkitURL).createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = safeFileName;
      link.click();

      // Clean up object URL
      setTimeout(() => {
        (window.URL || (window as any).webkitURL).revokeObjectURL(objectUrl);
      }, 100);
    }
  }
  InActiveStatus(toEmail: string): Observable<boolean> {
    return this.http.post<boolean>(`${environment.apiBaseUrl}/dashboard/IsInactive`, JSON.stringify(toEmail));
  }
  sendEmail(emailData: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/SendMail`, emailData);
  }

  addQRSEngagement(programAdmin: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/AddQRSEngagement`, programAdmin);
  }

  private surveyQuestions$: Observable<any> | null = null;
  private currentPeriod$: Observable<string> | null = null;
  
  // Program info caching (10-minute TTL)
  private programInfo$: Observable<any> | null = null;
  private programInfoCacheTime = 0;
  private PROGRAM_INFO_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

  getQRSSurveyQuestions(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/dashboard/GetQRSSurveyQuetions`);
  }


  getCurrentPeriod(): Observable<string> {
    return this.http.get<string>(`${environment.apiBaseUrl}/dashboard/GetCurrentPeriod`);
  }

  getEngagementSurveyQADetails(engagementId: number): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/dashboard/GetEngagementSurveyQADetails`, {
      params: { engagementId }
    });
  }

  updateSurveyQADetails(request: any): Observable<boolean> {
    return this.http.post<boolean>(`${environment.apiBaseUrl}/dashboard/UpdateSurveyQADetails`, request);
  }

  getQRSAudit(engagementId: number | null): Observable<any[]> {
    const params: any = {};
    if (engagementId !== null) params.engagementId = engagementId;
    // if (qstId !== null) params.qstId = qstId;
    // if (period !== null) params.period = period;
    return this.http.get<any[]>(`${environment.apiBaseUrl}/dashboard/GetQRSAudit`, { params });
  }

  UploadSurveyDocument(EngagementId: any, files: File[]): Observable<any> {
    const formData = new FormData();

    formData.append('PROGRAM_ID', '16');
    formData.append('ENGAGEMENT_ID', EngagementId || '');

    files.forEach(file => {
      formData.append('files', file, file.name);
    });

    return this.http.post(`${environment.apiBaseUrl}/Document/UploadSurveyDocument`, formData);
  }

  DeleteSurveyDocument(location: string): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/Document/DeleteSurveyDocument`, JSON.stringify(location), {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  finalizeQRSSurvey(EngagementId: number, PeriodId: string, QMT_ID: number): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/FinalizeQRSSurvey`, { EngagementId, PeriodId, QMT_ID });
  }

  //  QRSSaveSurveyComments(EngagementId: number, PeriodId: string, Comments: string, QST_ID: number, UserName: string): Observable<any> {
  //   return this.http.post(`${environment.apiBaseUrl}/dashboard/QRSSaveSurveyComments`, { EngagementId, PeriodId, Comments, QST_ID, UserName });
  // }

  // getQrsSurveyComments(engagementId: number, surveyId?: number): Observable<any> {
  //   const params: any = { engagementId };
  //   if (surveyId) {
  //     params.SurveyId = surveyId;
  //   }
  //   return this.http.get(`${environment.apiBaseUrl}/dashboard/GetQrsSurveyComments`, { params });
  // }

  qrsSaveNote(EngagementId: number, Notes: string, MainPage: string, SubPage: string, QmtId?: number, NoteTypeId?: number): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/dashboard/QRSSaveSurveyNotes`, { EngagementId, Notes, MainPage, SubPage, QmtId, NoteTypeId });
  }

  qrsUpdateNote(payload: { NoteId: number | string; EngagementId: number; Notes: string; MainPage: string; SubPage: string; QmtId?: number }): Observable<boolean> {
    return this.http.put<boolean>(`${environment.apiBaseUrl}/dashboard/QRSUpdateSurveyNotes`, payload);
  }

  qrsDeleteNote(engagementId: number, noteTypeId: number = 1, noteId: number): Observable<boolean> {
    return this.http.delete<boolean>(`${environment.apiBaseUrl}/dashboard/QRSDeleteSurveyNotes`, { params: { engagementId, noteTypeId, noteId } });
  }


  getQrsNotes(engagementId: number, noteTypeId?: number): Observable<any> {
    const params: any = { engagementId };
    if (noteTypeId) {
      params.noteTypeId = noteTypeId;
    }
    return this.http.get(`${environment.apiBaseUrl}/dashboard/GetQrsSurveyNotes`, { params });
  }

}

export interface OpLeaderMasterData {
  Offeringsportfolio: Array<any>;
  Offerings: Array<any>;
  SolutionMasterList: Array<any>;
  OPDashboardStaticData: Array<any>;
  OPDashboardMasterData: Array<any>;
  OPDashboardSentMailCountData: Array<any>;
  OPDashboardRolling13Periods: Array<any>;
  OPDashboardPeriodsWithDates: Array<any>;
  OPDashboardTextLabelChanges: Array<any>;
  ProgramList: Array<any>;
}
export interface EmployeeImageData {
  EngagementLeader: string;
  EngagementLeaderEmail: string;
  EngagementLeaderImage: string;
  EngagementLeaderImageBytes: string;
}
